import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getStoredWorkspace,
  persistWorkspace,
  resolveWorkspace,
} from './workspaceScope';
import { buildApiUrl, getApiHeaders } from '../config';
import { getAuthToken } from '../auth/authUtils';

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ initialWorkspace, children }) {
  const [workspace, setWorkspaceState] = useState(() => {
    const stored = getStoredWorkspace();
    const initial = resolveWorkspace(initialWorkspace || stored);
    return stored.exchange === initial.exchange && stored.marketType === initial.marketType
      ? { ...stored, ...initial } : initial;
  });
  const [scopeOptions, setScopeOptions] = useState([]);
  const [scopeSource, setScopeSource] = useState('loading');
  const [scopeRevision, setScopeRevision] = useState(0);
  const refreshScopes = useCallback(() => setScopeRevision(value => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function loadBackendScopes() {
      try {
        const response = await fetch(buildApiUrl('/api/bot/ui/orchestration/symbol-universe/scopes'), {
          headers: getApiHeaders({ Authorization: `Bearer ${getAuthToken()}` }),
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(`Scope API returned ${response.status}`);
        const rows = await response.json();
        if (!Array.isArray(rows)) throw new Error('Scope API returned an invalid response');
        if (cancelled) return;
        setScopeOptions(rows);
        setScopeSource('backend');
        if (rows.length === 0) {
          setWorkspaceState(resolveWorkspace());
          return;
        }
        setWorkspaceState(current => {
          const stillActive = rows.some(row =>
            row.exchange === current.exchange && row.marketType === current.marketType);
          const resolved = resolveWorkspace(stillActive ? rows.find(row =>
            row.exchange === current.exchange && row.marketType === current.marketType) : rows[0]);
          persistWorkspace(resolved);
          return resolved;
        });
      } catch (error) {
        if (!cancelled) setScopeSource('local-storage-fallback');
      }
    }
    loadBackendScopes();
    return () => { cancelled = true; };
  }, [scopeRevision]);

  useEffect(() => {
    const resolvedInitialWorkspace = resolveWorkspace(initialWorkspace || getStoredWorkspace());
    setWorkspaceState((prevWorkspace) => {
      if (
        prevWorkspace.exchange === resolvedInitialWorkspace.exchange &&
        prevWorkspace.marketType === resolvedInitialWorkspace.marketType
      ) {
        return prevWorkspace;
      }

      return resolvedInitialWorkspace;
    });
  }, [initialWorkspace]);

  useEffect(() => {
    if (workspace?.exchange && workspace?.marketType) {
      console.debug("WorkspaceContext active workspace", workspace);
    }
  }, [workspace]);

  const updateWorkspace = useCallback((nextWorkspace) => {
    const next = resolveWorkspace({
      ...workspace,
      ...nextWorkspace,
    });
    const metadata = scopeOptions.find(row => row.exchange === next.exchange && row.marketType === next.marketType);
    const resolved = persistWorkspace(metadata || next);
    setWorkspaceState((prevWorkspace) => {
      if (
        prevWorkspace.exchange === resolved.exchange &&
        prevWorkspace.marketType === resolved.marketType
      ) {
        return prevWorkspace;
      }

      return resolved;
    });
    return resolved;
  }, [workspace, scopeOptions]);

  const value = useMemo(
    () => ({
      workspace,
      exchange: workspace.exchange,
      marketType: workspace.marketType,
      scopeOptions,
      scopeSource,
      setWorkspace: updateWorkspace,
      refreshScopes,
    }),
    [workspace, scopeOptions, scopeSource, updateWorkspace, refreshScopes]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
